/**
 * PerformanceDashboard - Workflow performance monitoring
 * ZAPHUB 2.0 - Flame graphs, bottlenecks, optimization tips
 */

import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import {
  Flame,
  Zap,
  TrendingDown,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  Clock,
  MemoryStick,
  Activity,
  Target,
  Lightbulb,
  ArrowRight,
  Download,
} from 'lucide-react';
import type { PerformanceProfile, PerformanceBottleneck, PerformanceRecommendation } from '@shared/types';

interface PerformanceDashboardProps {
  workflowId?: string;
  executionId?: string;
}

export const PerformanceDashboard: React.FC<PerformanceDashboardProps> = ({
  workflowId = 'demo-workflow',
  executionId,
}) => {
  const [profile, setProfile] = useState<PerformanceProfile | null>(null);
  const [trends, setTrends] = useState<any>(null);

  useEffect(() => {
    if (executionId) {
      fetchProfile();
    }
    fetchTrends();
  }, [executionId, workflowId]);

  const fetchProfile = async () => {
    // Mock data
    const mockProfile: PerformanceProfile = {
      id: 'profile-1',
      executionId: executionId || 'exec-1',
      workflowId,
      startTime: Date.now() - 15000,
      endTime: Date.now(),
      totalDuration: 15000,
      nodeMetrics: [
        {
          nodeId: 'node-1',
          duration: 2000,
          memoryUsed: 50 * 1024 * 1024, // 50MB
          cpuTime: 1800,
          networkLatency: 200,
          apiCalls: 1,
          startTime: Date.now() - 15000,
          endTime: Date.now() - 13000,
        },
        {
          nodeId: 'node-2',
          duration: 8000,
          memoryUsed: 200 * 1024 * 1024, // 200MB
          cpuTime: 7500,
          networkLatency: 1500,
          apiCalls: 5,
          startTime: Date.now() - 13000,
          endTime: Date.now() - 5000,
        },
        {
          nodeId: 'node-3',
          duration: 3000,
          memoryUsed: 80 * 1024 * 1024, // 80MB
          cpuTime: 2800,
          networkLatency: 300,
          apiCalls: 2,
          startTime: Date.now() - 5000,
          endTime: Date.now() - 2000,
        },
        {
          nodeId: 'node-4',
          duration: 2000,
          memoryUsed: 40 * 1024 * 1024, // 40MB
          cpuTime: 1900,
          networkLatency: 100,
          apiCalls: 1,
          startTime: Date.now() - 2000,
          endTime: Date.now(),
        },
      ],
      memoryUsage: {
        peak: 200 * 1024 * 1024,
        average: 92.5 * 1024 * 1024,
        total: 370 * 1024 * 1024,
      },
      bottlenecks: [
        {
          type: 'slow_node',
          nodeId: 'node-2',
          severity: 'critical',
          description: 'Node takes 8000ms (53% of total time)',
          impact: 'high',
          suggestedFix: 'Consider optimizing API calls or implementing caching',
        },
        {
          type: 'high_memory',
          nodeId: 'node-2',
          severity: 'warning',
          description: 'Node uses 200MB memory',
          impact: 'medium',
          suggestedFix: 'Reduce payload size or use streaming for large data',
        },
        {
          type: 'high_latency',
          nodeId: 'node-2',
          severity: 'warning',
          description: 'High network latency: 1500ms',
          impact: 'medium',
          suggestedFix: 'Consider caching API responses or using a CDN',
        },
      ],
      recommendations: [
        {
          type: 'optimization',
          priority: 'high',
          title: 'Optimize slow node',
          description: 'Node 2 is the primary bottleneck taking 53% of execution time',
          estimatedImpact: {
            timeReduction: '40%',
            costReduction: '30%',
          },
          implementation: [
            'Implement caching for API responses',
            'Reduce number of API calls through batching',
            'Consider parallel execution where possible',
          ],
        },
        {
          type: 'caching',
          priority: 'high',
          title: 'Add response caching',
          description: 'Multiple API calls detected that could benefit from caching',
          estimatedImpact: {
            timeReduction: '60%',
            costReduction: '70%',
          },
          implementation: [
            'Implement Redis caching for API responses',
            'Set appropriate TTL based on data freshness requirements',
            'Use cache-first strategy for static data',
          ],
        },
        {
          type: 'memory_optimization',
          priority: 'medium',
          title: 'Reduce memory usage',
          description: 'Peak memory usage of 200MB detected',
          estimatedImpact: {
            costReduction: '20%',
          },
          implementation: [
            'Stream large files instead of loading into memory',
            'Clean up unused variables',
            'Reduce payload sizes',
          ],
        },
      ],
    };

    setProfile(mockProfile);
  };

  const fetchTrends = async () => {
    // Mock trend data
    const mockTrends = {
      dates: ['Dec 11', 'Dec 12', 'Dec 13', 'Dec 14', 'Dec 15', 'Dec 16', 'Dec 17'],
      durations: [18000, 17000, 16500, 15500, 15000, 14800, 15000],
      memoryUsage: [220, 210, 200, 195, 190, 188, 200],
      successRate: [92, 94, 95, 96, 97, 98, 97],
    };

    setTrends(mockTrends);
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'bg-red-100 text-red-700 border-red-200';
      case 'warning':
        return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'info':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'destructive';
      case 'medium':
        return 'default';
      case 'low':
        return 'secondary';
      default:
        return 'secondary';
    }
  };

  const formatBytes = (bytes: number) => {
    return (bytes / 1024 / 1024).toFixed(2) + ' MB';
  };

  const formatDuration = (ms: number) => {
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  if (!profile) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Activity className="h-12 w-12 text-gray-400 mx-auto mb-3 animate-pulse" />
          <p className="text-gray-600">Loading performance data...</p>
        </div>
      </div>
    );
  }

  const nodeChartData = profile.nodeMetrics.map((node) => ({
    name: `Node ${node.nodeId.split('-')[1]}`,
    duration: node.duration,
    memory: node.memoryUsed / 1024 / 1024, // Convert to MB
    apiCalls: node.apiCalls,
  }));

  const COLORS = ['#8B5CF6', '#EC4899', '#F59E0B', '#10B981', '#3B82F6'];

  return (
    <div className="h-full overflow-auto bg-gray-50 p-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl mb-2">Performance Dashboard</h1>
          <p className="text-gray-600">Analyze and optimize workflow performance</p>
        </div>
        <Button variant="outline">
          <Download className="h-4 w-4 mr-2" />
          Export Report
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-purple-100 flex items-center justify-center">
              <Clock className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Duration</p>
              <p className="text-2xl">{formatDuration(profile.totalDuration)}</p>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <MemoryStick className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Peak Memory</p>
              <p className="text-2xl">{formatBytes(profile.memoryUsage.peak)}</p>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-green-100 flex items-center justify-center">
              <Activity className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Nodes Executed</p>
              <p className="text-2xl">{profile.nodeMetrics.length}</p>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-orange-100 flex items-center justify-center">
              <AlertTriangle className="h-5 w-5 text-orange-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Bottlenecks</p>
              <p className="text-2xl">{profile.bottlenecks?.length || 0}</p>
            </div>
          </div>
        </Card>
      </div>

      <Tabs defaultValue="overview" className="w-full">
        <TabsList>
          <TabsTrigger value="overview">
            <Activity className="h-4 w-4 mr-2" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="flame-graph">
            <Flame className="h-4 w-4 mr-2" />
            Flame Graph
          </TabsTrigger>
          <TabsTrigger value="bottlenecks">
            <AlertTriangle className="h-4 w-4 mr-2" />
            Bottlenecks ({profile.bottlenecks?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="recommendations">
            <Lightbulb className="h-4 w-4 mr-2" />
            Recommendations ({profile.recommendations?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="trends">
            <TrendingUp className="h-4 w-4 mr-2" />
            Trends
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6 mt-6">
          {/* Node Performance */}
          <Card className="p-6">
            <h3 className="text-lg mb-4">Node Performance Breakdown</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={nodeChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis yAxisId="left" />
                <YAxis yAxisId="right" orientation="right" />
                <Tooltip />
                <Legend />
                <Bar yAxisId="left" dataKey="duration" fill="#8B5CF6" name="Duration (ms)" />
                <Bar yAxisId="right" dataKey="memory" fill="#EC4899" name="Memory (MB)" />
              </BarChart>
            </ResponsiveContainer>
          </Card>

          {/* Node Details Table */}
          <Card className="p-6">
            <h3 className="text-lg mb-4">Node Details</h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-3">Node</th>
                    <th className="text-right py-2 px-3">Duration</th>
                    <th className="text-right py-2 px-3">Memory</th>
                    <th className="text-right py-2 px-3">Latency</th>
                    <th className="text-right py-2 px-3">API Calls</th>
                    <th className="text-right py-2 px-3">% of Total</th>
                  </tr>
                </thead>
                <tbody>
                  {profile.nodeMetrics.map((node) => (
                    <tr key={node.nodeId} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-3 font-mono text-sm">{node.nodeId}</td>
                      <td className="py-3 px-3 text-right">{formatDuration(node.duration)}</td>
                      <td className="py-3 px-3 text-right">{formatBytes(node.memoryUsed)}</td>
                      <td className="py-3 px-3 text-right">{node.networkLatency}ms</td>
                      <td className="py-3 px-3 text-right">{node.apiCalls}</td>
                      <td className="py-3 px-3 text-right">
                        <Badge variant="secondary">
                          {((node.duration / profile.totalDuration) * 100).toFixed(1)}%
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="flame-graph" className="space-y-6 mt-6">
          <Card className="p-6">
            <h3 className="text-lg mb-4">Execution Flame Graph</h3>
            <div className="space-y-2">
              {profile.nodeMetrics.map((node, idx) => {
                const widthPercent = (node.duration / profile.totalDuration) * 100;
                return (
                  <div key={node.nodeId} className="relative">
                    <div
                      className="h-12 rounded flex items-center px-4 text-white"
                      style={{
                        width: `${widthPercent}%`,
                        backgroundColor: COLORS[idx % COLORS.length],
                      }}
                    >
                      <span className="font-mono text-sm">{node.nodeId}</span>
                      <span className="ml-auto text-sm">{formatDuration(node.duration)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-4 text-sm text-gray-600">
              <p>Width represents execution time relative to total workflow duration</p>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="bottlenecks" className="space-y-4 mt-6">
          {profile.bottlenecks && profile.bottlenecks.length > 0 ? (
            profile.bottlenecks.map((bottleneck, idx) => (
              <Card key={idx} className={`p-6 border ${getSeverityColor(bottleneck.severity)}`}>
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <AlertTriangle className="h-6 w-6" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant={bottleneck.severity === 'critical' ? 'destructive' : 'secondary'}>
                        {bottleneck.severity.toUpperCase()}
                      </Badge>
                      <Badge variant="outline">{bottleneck.type.replace('_', ' ')}</Badge>
                      <Badge variant="outline">Impact: {bottleneck.impact}</Badge>
                    </div>
                    <h4 className="text-lg mb-2">Node: {bottleneck.nodeId}</h4>
                    <p className="mb-3">{bottleneck.description}</p>
                    <div className="bg-white/50 rounded p-3 border">
                      <p className="text-sm font-medium mb-1">Suggested Fix:</p>
                      <p className="text-sm">{bottleneck.suggestedFix}</p>
                    </div>
                  </div>
                </div>
              </Card>
            ))
          ) : (
            <Card className="p-12 text-center">
              <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-xl mb-2">No Bottlenecks Detected</h3>
              <p className="text-gray-600">
                Your workflow is performing optimally! 🎉
              </p>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="recommendations" className="space-y-4 mt-6">
          {profile.recommendations && profile.recommendations.length > 0 ? (
            profile.recommendations.map((rec, idx) => (
              <Card key={idx} className="p-6">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <div className="h-10 w-10 rounded-lg bg-purple-100 flex items-center justify-center">
                      <Lightbulb className="h-5 w-5 text-purple-600" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-3">
                      <Badge variant={getPriorityColor(rec.priority)}>
                        {rec.priority.toUpperCase()} PRIORITY
                      </Badge>
                      <Badge variant="outline">{rec.type.replace('_', ' ')}</Badge>
                    </div>
                    <h4 className="text-lg mb-2">{rec.title}</h4>
                    <p className="text-gray-600 mb-4">{rec.description}</p>

                    <div className="grid grid-cols-2 gap-4 mb-4">
                      {rec.estimatedImpact.timeReduction && (
                        <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                          <div className="flex items-center gap-2">
                            <TrendingDown className="h-5 w-5 text-green-600" />
                            <div>
                              <p className="text-sm text-green-600">Time Reduction</p>
                              <p className="text-xl text-green-700">{rec.estimatedImpact.timeReduction}</p>
                            </div>
                          </div>
                        </div>
                      )}
                      {rec.estimatedImpact.costReduction && (
                        <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                          <div className="flex items-center gap-2">
                            <TrendingDown className="h-5 w-5 text-blue-600" />
                            <div>
                              <p className="text-sm text-blue-600">Cost Reduction</p>
                              <p className="text-xl text-blue-700">{rec.estimatedImpact.costReduction}</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="bg-gray-50 rounded-lg p-4 border">
                      <p className="font-medium mb-2">Implementation Steps:</p>
                      <ul className="space-y-2">
                        {rec.implementation.map((step, stepIdx) => (
                          <li key={stepIdx} className="flex items-start gap-2 text-sm">
                            <ArrowRight className="h-4 w-4 text-purple-600 flex-shrink-0 mt-0.5" />
                            <span>{step}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="mt-4">
                      <Button>
                        <Zap className="h-4 w-4 mr-2" />
                        Apply Optimization
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            ))
          ) : (
            <Card className="p-12 text-center">
              <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-xl mb-2">No Recommendations</h3>
              <p className="text-gray-600">
                Your workflow is already well-optimized!
              </p>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="trends" className="space-y-6 mt-6">
          {trends && (
            <>
              <Card className="p-6">
                <h3 className="text-lg mb-4">Execution Duration Trend</h3>
                <ResponsiveContainer width="100%" height={250}>
                  <AreaChart data={trends.dates.map((date: string, idx: number) => ({
                    date,
                    duration: trends.durations[idx],
                  }))}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Area type="monotone" dataKey="duration" stroke="#8B5CF6" fill="#8B5CF6" fillOpacity={0.6} />
                  </AreaChart>
                </ResponsiveContainer>
              </Card>

              <Card className="p-6">
                <h3 className="text-lg mb-4">Success Rate Trend</h3>
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={trends.dates.map((date: string, idx: number) => ({
                    date,
                    rate: trends.successRate[idx],
                  }))}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis domain={[0, 100]} />
                    <Tooltip />
                    <Line type="monotone" dataKey="rate" stroke="#10B981" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </Card>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};
